/**
 * Pure event calendar/clock/instant contract.
 *
 * Keep api/src/lib/event-temporal.ts and frontend/src/lib/eventTemporal.ts
 * identical. Neither deployment can import the other package; tests check parity.
 * All-day identity requires an explicit true flag. Legacy midnight inference,
 * source adjudication, telemetry and writes belong to the calling layer.
 */
export const EVENT_TIME_ZONE = 'America/New_York';

export interface EventTemporalInput {
  startUtc: string;
  endUtc?: string | null;
  isAllDay?: boolean | null;
  /** `false` on an all-day row means the source gave no time yet: labelled "Time TBA", not "All day". */
  timeAnnounced?: boolean | null;
  /** The source's own date words. Only `monthOnlyLabel` reads it, to tell a month-only span from a real run. */
  sourceDateText?: string | null;
}

export interface CalendarDateParts {
  year: number;
  month: number;
  day: number;
}

export interface EventDateTimeValue {
  /** Canonical UTC representation of the same instant, at JS millisecond precision. */
  isoUtc: string;
  dateKey: string;
  /** New York HH:mm, not a localized display label. */
  clock: string;
  /** datetime-local input value; retains nonzero seconds/milliseconds. */
  localInput: string;
  /** Actual IANA offset, normally -04:00 or -05:00; historical seconds are retained. */
  offset: string;
}

export type EventTemporalErrorReason =
  | 'invalid-date'
  | 'invalid-clock'
  | 'invalid-instant'
  | 'invalid-local-input'
  | 'invalid-offset'
  | 'offset-mismatch'
  | 'nonexistent-local-time'
  | 'ambiguous-local-time'
  | 'invalid-range'
  | 'out-of-range';

export type EventTemporalResult<T> =
  | { ok: true; value: T }
  | {
    ok: false;
    reason: EventTemporalErrorReason;
    /** Possible New York instants, earliest first; no candidate is chosen implicitly. */
    candidates?: readonly EventDateTimeValue[];
  };

export interface EventUtcRange {
  fromUtc: string;
  /** Exclusive upper boundary. */
  toUtc: string;
}

export type EventDateFormatOptions = Pick<
  Intl.DateTimeFormatOptions, 'weekday' | 'year' | 'month' | 'day'
>;
export type EventClockFormatOptions = Pick<
  Intl.DateTimeFormatOptions, 'hour' | 'minute' | 'second' | 'hour12' | 'hourCycle'
>;
export interface EventRangeFormatOptions {
  date?: EventDateFormatOptions;
  clock?: EventClockFormatOptions;
  /** Default false: display the stored end date as written. Set true for a known exclusive end. */
  allDayEndExclusive?: boolean;
}

interface ClockParts {
  hour: number;
  minute: number;
  second: number;
  millisecond: number;
}
type LocalParts = CalendarDateParts & ClockParts;

const MS_PER_DAY = 86_400_000;
const PARTS_FORMATTER = new Intl.DateTimeFormat('en-US', {
  timeZone: EVENT_TIME_ZONE,
  calendar: 'gregory',
  numberingSystem: 'latn',
  era: 'short',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hourCycle: 'h23'
});

function pad2(value: number): string {
  return String(value).padStart(2, '0');
}

function daysInMonth(year: number, month: number): number {
  if (month === 2) return year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0) ? 29 : 28;
  return [4, 6, 9, 11].includes(month) ? 30 : 31;
}

/** Strict Gregorian YYYY-MM-DD, years 0001..9999; never permits Date's rollover. */
export function parseDateLabel(value: string): CalendarDateParts | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match || match[0] !== value) return null;
  const year = Number(match[1]), month = Number(match[2]), day = Number(match[3]);
  if (year < 1 || month < 1 || month > 12 || day < 1 || day > daysInMonth(year, month)) return null;
  return { year, month, day };
}

function dateLabel(parts: CalendarDateParts): string {
  return `${String(parts.year).padStart(4, '0')}-${pad2(parts.month)}-${pad2(parts.day)}`;
}

function sourceDateLabel(value: string): string | null {
  const match = /^(\d{4}-\d{2}-\d{2})/.exec(value);
  if (value.length !== 10 && value[10] !== 'T' && value[10] !== 't') return null;
  return match && parseDateLabel(match[1]) ? match[1] : null;
}

/** UTC is used only as a Gregorian arithmetic coordinate, not as the event timezone. */
function calendarMs(parts: CalendarDateParts, clock?: ClockParts): number {
  const date = new Date(0);
  // Date.UTC(1, ...) would mean 1901. setUTCFullYear preserves years below 100.
  date.setUTCFullYear(parts.year, parts.month - 1, parts.day);
  date.setUTCHours(clock?.hour ?? 0, clock?.minute ?? 0, clock?.second ?? 0, clock?.millisecond ?? 0);
  return date.getTime();
}

/** Whole calendar days since 1970-01-01; suitable for date differences across DST. */
export function calendarDateIndex(value: string): number | null {
  const parts = parseDateLabel(value);
  return parts ? calendarMs(parts) / MS_PER_DAY : null;
}

/** Sunday=0..Saturday=6, independent of browser/process timezone. */
export function calendarWeekday(value: string): number | null {
  const index = calendarDateIndex(value);
  return index === null ? null : ((index + 4) % 7 + 7) % 7;
}

export function addCalendarDays(value: string, days: number): string | null {
  const index = calendarDateIndex(value);
  if (index === null || !Number.isSafeInteger(days)) return null;
  const date = new Date((index + days) * MS_PER_DAY);
  const year = date.getUTCFullYear();
  if (!Number.isFinite(date.getTime()) || year < 1 || year > 9999) return null;
  return dateLabel({ year, month: date.getUTCMonth() + 1, day: date.getUTCDate() });
}

/** Whole-month arithmetic clamps the day to the target month, rather than rolling into another month. */
export function addCalendarMonths(value: string, months: number): string | null {
  const parts = parseDateLabel(value);
  if (!parts || !Number.isSafeInteger(months)) return null;
  const index = (parts.year - 1) * 12 + parts.month - 1 + months;
  if (index < 0 || index >= 9999 * 12) return null;
  const year = Math.floor(index / 12) + 1, month = index % 12 + 1;
  return dateLabel({ year, month, day: Math.min(parts.day, daysInMonth(year, month)) });
}

function parseClock(value: string): ClockParts | null {
  const match = /^(\d{2}):(\d{2})(?::(\d{2})(?:\.(\d{1,3}))?)?$/.exec(value);
  if (!match || match[0] !== value) return null;
  const hour = Number(match[1]), minute = Number(match[2]), second = Number(match[3] ?? 0);
  if (hour > 23 || minute > 59 || second > 59) return null;
  return { hour, minute, second, millisecond: Number((match[4] ?? '').padEnd(3, '0')) };
}

/** Minute of day for a valid HH:mm[:ss[.SSS]]; seconds are validated but not included. */
export function clockMinutes(value: string): number | null {
  const parts = parseClock(value);
  return parts ? parts.hour * 60 + parts.minute : null;
}

/** Integer minute of day -> HH:mm. Does not wrap out-of-range values. */
export function clockFromMinutes(value: number): string | null {
  return Number.isInteger(value) && value >= 0 && value < 1440
    ? `${pad2(Math.floor(value / 60))}:${pad2(value % 60)}`
    : null;
}

function offsetSeconds(value: string): number | null {
  if (value === 'Z' || value === 'z') return 0;
  const match = /^([+-])(\d{2}):?(\d{2})(?::?(\d{2}))?$/.exec(value);
  if (!match || match[0] !== value) return null;
  const hour = Number(match[2]), minute = Number(match[3]), second = Number(match[4] ?? 0);
  if (hour > 23 || minute > 59 || second > 59) return null;
  return (match[1] === '-' ? -1 : 1) * (hour * 3600 + minute * 60 + second);
}

function offsetLabel(seconds: number): string {
  const absolute = Math.abs(seconds);
  const label = `${seconds < 0 ? '-' : '+'}${pad2(Math.floor(absolute / 3600))}:${pad2(Math.floor(absolute / 60) % 60)}`;
  return absolute % 60 === 0 ? label : `${label}:${pad2(absolute % 60)}`;
}

/** Only explicitly zoned ISO instants. A date-only or unzoned string is not an instant. */
function instantMs(value: string): number | null {
  const match = /^(\d{4}-\d{2}-\d{2})T(\d{2}:\d{2})(?::(\d{2})(?:\.(\d+))?)?(Z|[+-]\d{2}:?\d{2})$/i.exec(value);
  if (!match || match[0] !== value) return null;
  const date = parseDateLabel(match[1]);
  // ISO feeds may have sub-millisecond precision; retain the same JS instant as Date.parse.
  const clock = parseClock(`${match[2]}${match[3] ? `:${match[3]}` : ''}${match[4] ? `.${match[4].slice(0, 3)}` : ''}`);
  // ISO permits 24:00 only as exact end-of-day. It is an instant spelling,
  // not an acceptable activity-local input clock (parseClock still rejects it).
  const endOfDay = match[2] === '24:00' && Number(match[3] ?? 0) === 0 && !/[1-9]/.test(match[4] ?? '');
  const offset = offsetSeconds(match[5]);
  if (!date || (!clock && !endOfDay) || offset === null) return null;
  const ms = Date.parse(value);
  return Number.isFinite(ms) ? ms : null;
}

function localParts(ms: number): LocalParts | null {
  if (!Number.isFinite(ms)) return null;
  const fields = PARTS_FORMATTER.formatToParts(ms);
  if (fields.find((part) => part.type === 'era')?.value !== 'AD') return null;
  const number = (type: Intl.DateTimeFormatPartTypes) =>
    Number(fields.find((part) => part.type === type)?.value);
  const parts = {
    year: number('year'), month: number('month'), day: number('day'),
    hour: number('hour'), minute: number('minute'), second: number('second'),
    millisecond: new Date(ms).getUTCMilliseconds()
  };
  return parseDateLabel(dateLabel(parts)) ? parts : null;
}

function clockLabel(parts: ClockParts): string {
  return `${pad2(parts.hour)}:${pad2(parts.minute)}`;
}

function inputLabel(parts: LocalParts): string {
  const seconds = parts.second || parts.millisecond ? `:${pad2(parts.second)}` : '';
  const milliseconds = parts.millisecond ? `.${String(parts.millisecond).padStart(3, '0')}` : '';
  return `${dateLabel(parts)}T${clockLabel(parts)}${seconds}${milliseconds}`;
}

function valueForInstant(ms: number, parts: LocalParts): EventDateTimeValue | null {
  const year = new Date(ms).getUTCFullYear();
  if (year < 1 || year > 9999) return null;
  return {
    isoUtc: new Date(ms).toISOString(),
    dateKey: dateLabel(parts),
    clock: clockLabel(parts),
    localInput: inputLabel(parts),
    offset: offsetLabel((calendarMs(parts, parts) - ms) / 1000)
  };
}

/**
 * Read a trusted UTC/offset instant without reinterpreting its written clock as NY.
 * Retain value.offset alongside value.localInput to round-trip a fall-back instant.
 */
export function instantToEventDateTime(value: string): EventTemporalResult<EventDateTimeValue> {
  const ms = instantMs(value);
  if (ms === null) return { ok: false, reason: 'invalid-instant' };
  const parts = localParts(ms);
  const result = parts && valueForInstant(ms, parts);
  return result ? { ok: true, value: result } : { ok: false, reason: 'out-of-range' };
}

/** All-day dates use only the validated source prefix; timed dates are the NY date of the instant. */
export function eventDateKey(event: EventTemporalInput): string | null {
  if (event.isAllDay === true) return sourceDateLabel(event.startUtc);
  const result = instantToEventDateTime(event.startUtc);
  return result.ok ? result.value.dateKey : null;
}

/** New York HH:mm; all-day and invalid events have no clock. */
export function eventClock(event: EventTemporalInput): string | null {
  if (event.isAllDay === true) return null;
  const result = instantToEventDateTime(event.startUtc);
  return result.ok ? result.value.clock : null;
}

export function eventClockMinutes(event: EventTemporalInput): number | null {
  const clock = eventClock(event);
  return clock === null ? null : clockMinutes(clock);
}

/**
 * Resolve a source-grounded NY date and wall clock. Sample IANA offsets on both
 * sides of the date, then retain only exact local round-trips. Unlike a fixed-point
 * offset guess, this detects both spring gaps and fall folds.
 *
 * explicitOffset is source evidence, not a preference: it must match a possible
 * NY instant. It cannot force a nonexistent clock or "fix" a legacy offset.
 */
export function resolveEventLocalDateTime(
  date: string, clock: string, explicitOffset?: string | null
): EventTemporalResult<EventDateTimeValue> {
  const dateParts = parseDateLabel(date);
  if (!dateParts) return { ok: false, reason: 'invalid-date' };
  const clockParts = parseClock(clock);
  if (!clockParts) return { ok: false, reason: 'invalid-clock' };
  const requestedOffset = explicitOffset == null ? null : offsetSeconds(explicitOffset);
  if (explicitOffset != null && requestedOffset === null) return { ok: false, reason: 'invalid-offset' };
  const target = { ...dateParts, ...clockParts };
  const localAsUtc = calendarMs(dateParts, clockParts);
  const offsets = new Set<number>();
  for (const delta of [-MS_PER_DAY, 0, MS_PER_DAY]) {
    const ms = localAsUtc + delta;
    const parts = localParts(ms);
    if (parts) offsets.add((calendarMs(parts, parts) - ms) / 1000);
  }
  const candidates: EventDateTimeValue[] = [];
  let outOfRange = false;
  for (const offset of offsets) {
    const ms = localAsUtc - offset * 1000;
    const parts = localParts(ms);
    if (!parts || calendarMs(parts, parts) !== calendarMs(target, target)) continue;
    const value = valueForInstant(ms, parts);
    if (value) candidates.push(value);
    else outOfRange = true;
  }
  candidates.sort((a, b) => Date.parse(a.isoUtc) - Date.parse(b.isoUtc));
  if (candidates.length === 0) {
    return { ok: false, reason: outOfRange ? 'out-of-range' : 'nonexistent-local-time' };
  }
  if (requestedOffset !== null) {
    const selected = candidates.find((candidate) => offsetSeconds(candidate.offset) === requestedOffset);
    return selected
      ? { ok: true, value: selected }
      : { ok: false, reason: 'offset-mismatch', candidates };
  }
  return candidates.length === 1
    ? { ok: true, value: candidates[0] }
    : { ok: false, reason: 'ambiguous-local-time', candidates };
}

/** Parse a datetime-local input in NY, not the browser zone. Zoned feed values use instantToEventDateTime. */
export function resolveEventDateTimeInput(
  input: string, explicitOffset?: string | null
): EventTemporalResult<EventDateTimeValue> {
  const match = /^(\d{4}-\d{2}-\d{2})T(.+)$/.exec(input);
  return match && match[0] === input
    ? resolveEventLocalDateTime(match[1], match[2], explicitOffset)
    : { ok: false, reason: 'invalid-local-input' };
}

export function eventDayStart(date: string): EventTemporalResult<EventDateTimeValue> {
  return resolveEventLocalDateTime(date, '00:00');
}

/** Positive, half-open NY date range; each boundary is resolved separately across DST. */
export function eventDateRangeUtc(fromDate: string, toDateExclusive: string): EventTemporalResult<EventUtcRange> {
  if (!parseDateLabel(fromDate) || !parseDateLabel(toDateExclusive)) return { ok: false, reason: 'invalid-date' };
  if (toDateExclusive <= fromDate) return { ok: false, reason: 'invalid-range' };
  const from = eventDayStart(fromDate);
  if (!from.ok) return from;
  const to = eventDayStart(toDateExclusive);
  if (!to.ok) return to;
  return { ok: true, value: { fromUtc: from.value.isoUtc, toUtc: to.value.isoUtc } };
}

/** An NY day can be 23, 24 or 25 hours; toUtc is next calendar midnight, not +24 hours. */
export function eventDayBounds(date: string): EventTemporalResult<EventUtcRange> {
  if (!parseDateLabel(date)) return { ok: false, reason: 'invalid-date' };
  const next = addCalendarDays(date, 1);
  return next ? eventDateRangeUtc(date, next) : { ok: false, reason: 'out-of-range' };
}

function dateFormatter(options: EventDateFormatOptions | undefined, locale: string, timeZone: string): Intl.DateTimeFormat {
  return new Intl.DateTimeFormat(locale, {
    weekday: 'short', month: 'short', day: 'numeric', ...options,
    timeZone, calendar: 'gregory'
  });
}

/** Format a validated calendar label without treating it as an instant. Default: "Fri, Nov 6". */
export function formatDateLabel(date: string, options?: EventDateFormatOptions, locale = 'en-US'): string | null {
  const parts = parseDateLabel(date);
  return parts ? dateFormatter(options, locale, 'UTC').format(calendarMs(parts)) : null;
}

export function formatEventDate(event: EventTemporalInput, options?: EventDateFormatOptions, locale = 'en-US'): string | null {
  if (event.isAllDay === true) {
    const key = eventDateKey(event);
    return key === null ? null : formatDateLabel(key, options, locale);
  }
  const result = instantToEventDateTime(event.startUtc);
  return result.ok ? dateFormatter(options, locale, EVENT_TIME_ZONE).format(Date.parse(result.value.isoUtc)) : null;
}

function displayClock(ms: number, options: EventClockFormatOptions | undefined, locale: string): string {
  const { hour12 = true, hourCycle, ...fields } = options ?? {};
  return new Intl.DateTimeFormat(locale, {
    hour: 'numeric', minute: '2-digit', ...fields,
    hourCycle: hourCycle ?? (hour12 ? 'h12' : 'h23'),
    timeZone: EVENT_TIME_ZONE
  }).format(ms);
}

const MONTH_BY_NAME: Readonly<Record<string, number>> = {
  jan: 1, january: 1, feb: 2, february: 2, mar: 3, march: 3, apr: 4, april: 4, may: 5, jun: 6, june: 6,
  jul: 7, july: 7, aug: 8, august: 8, sep: 9, sept: 9, september: 9, oct: 10, october: 10,
  nov: 11, november: 11, dec: 12, december: 12
};
/** A quote that is a month and nothing else: "MARCH", "March 2027", "Mar.". Any other word or digit fails it. */
const MONTH_ONLY_QUOTE = /^\s*([A-Za-z]{3,9})\.?(?:\s*,?\s*(\d{4}))?\s*$/;

/**
 * "March 2027" for an all-day span the source dated only by month, otherwise null (#67).
 *
 * Some sources announce just "March 2027"; the extractor stores that as a span over the whole month, and reading it
 * as "Mar 1 – Mar 31" claims days nobody announced. All of these must hold:
 * - the row is all-day;
 * - it starts on the 1st of a month and ends on that month's last day, both read as source calendar dates;
 * - `sourceDateText` is a month name alone, optionally with a year, naming that month. Words such as "through",
 *   "until", "starting", "from", "now", a day number, a weekday or a second month make a run or a day, so no label.
 *
 * The year is always the stored start's. A printed year that disagrees with it gives no label.
 * `allDayEndExclusive` says the stored end is the day after the last included day.
 */
export function monthOnlyLabel(event: EventTemporalInput, options: { allDayEndExclusive?: boolean } = {}): string | null {
  if (event.isAllDay !== true || event.endUtc == null || typeof event.sourceDateText !== 'string') return null;
  const quote = MONTH_ONLY_QUOTE.exec(event.sourceDateText);
  const quoted = quote ? MONTH_BY_NAME[quote[1].toLowerCase()] : undefined;
  const startKey = eventDateKey(event);
  const endSource = eventDateKey({ startUtc: event.endUtc, isAllDay: true });
  const endKey = endSource !== null && options.allDayEndExclusive ? addCalendarDays(endSource, -1) : endSource;
  const start = startKey === null ? null : parseDateLabel(startKey);
  const end = endKey === null ? null : parseDateLabel(endKey);
  if (!quote || quoted === undefined || !start || !end) return null;
  if (start.day !== 1 || end.year !== start.year || end.month !== start.month) return null;
  if (end.day !== daysInMonth(end.year, end.month) || quoted !== start.month) return null;
  if (quote[2] !== undefined && Number(quote[2]) !== start.year) return null;
  return formatDateLabel(startKey!, { weekday: undefined, day: undefined, month: 'long', year: 'numeric' });
}

/** The label for a date with no clock: "Date TBA" for a month-only row, "Time TBA" when the source said so, otherwise "All day". */
function noClockLabel(event: EventTemporalInput, allDayEndExclusive = false): string {
  if (monthOnlyLabel(event, { allDayEndExclusive }) !== null) return 'Date TBA';
  return event.timeAnnounced === false ? 'Time TBA' : 'All day';
}

/** Timed events show their Eastern wall clock. All-day events say "All day", "Time TBA" when the source gave no time, or "Date TBA" for a month-only span. */
export function formatEventClock(event: EventTemporalInput, options?: EventClockFormatOptions, locale = 'en-US'): string | null {
  if (event.isAllDay === true) return eventDateKey(event) === null ? null : noClockLabel(event);
  const result = instantToEventDateTime(event.startUtc);
  return result.ok ? displayClock(Date.parse(result.value.isoUtc), options, locale) : null;
}

/**
 * Date plus clock/range, or date(s) plus "All day" / "Time TBA". Invalid/reversed ends return
 * null, not a plausible partial label. Stored all-day end dates are displayed
 * inclusively unless the caller explicitly identifies an exclusive end.
 */
export function formatEventRange(
  event: EventTemporalInput, options: EventRangeFormatOptions = {}, locale = 'en-US'
): string | null {
  const startKey = eventDateKey(event);
  if (startKey === null) return null;
  let endKey = event.endUtc == null ? startKey : eventDateKey({ ...event, startUtc: event.endUtc });
  if (endKey === null) return null;
  if (event.isAllDay === true) {
    if (event.endUtc != null && options.allDayEndExclusive) endKey = addCalendarDays(endKey, -1);
    if (endKey === null || endKey < startKey) return null;
    const monthLabel = monthOnlyLabel(event, { allDayEndExclusive: options.allDayEndExclusive });
    if (monthLabel !== null) return `${monthLabel} · Date TBA`;
    const startLabel = formatDateLabel(startKey, options.date, locale);
    const endLabel = formatDateLabel(endKey, options.date, locale);
    return `${startLabel}${endKey === startKey ? '' : `–${endLabel}`} · ${noClockLabel(event, options.allDayEndExclusive)}`;
  }
  const start = instantToEventDateTime(event.startUtc);
  const end = event.endUtc == null ? start : instantToEventDateTime(event.endUtc);
  if (!start.ok || !end.ok) return null;
  const startMs = Date.parse(start.value.isoUtc), endMs = Date.parse(end.value.isoUtc);
  if (endMs < startMs) return null;
  const startDate = formatEventDate(event, options.date, locale);
  const startClock = displayClock(startMs, options.clock, locale);
  if (endMs === startMs) return `${startDate} · ${startClock}`;
  const endClock = displayClock(endMs, options.clock, locale);
  if (endKey !== startKey) {
    const endDate = formatEventDate({ startUtc: end.value.isoUtc }, options.date, locale);
    return `${startDate} · ${startClock} – ${endDate} · ${endClock}`;
  }
  // A fall-back range can have equal clocks but different instants; show why.
  if (start.value.offset !== end.value.offset) {
    return `${startDate} · ${startClock} (UTC${start.value.offset})–${endClock} (UTC${end.value.offset})`;
  }
  return `${startDate} · ${startClock}–${endClock}`;
}
